// src/components/FutureIntegrations.js
import React from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  IntegrationInstructions, SmartToy, SettingsInputComponent,
  Psychology, Engineering, LocalLaundryService
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';

const FutureIntegrations = () => {
  const { t } = useI18n();
  const [selectedIntegration, setSelectedIntegration] = React.useState(null);

  const integrations = [
    {
      id: 'card-system',
      benefits: [
        'integrations_benefit_auto_tracking',
        'integrations_benefit_real_time', 
        'integrations_benefit_manual_elimination',
        'integrations_benefit_payroll_integration'
      ],
      status: 'planned',
      icon: <SmartToy />
    },
    {
      id: 'washing-machines',
      benefits: [
        'integrations_benefit_predictive_maintenance',
        'integrations_benefit_efficiency_tracking',
        'integrations_benefit_resource_monitoring',
        'integrations_benefit_quality_control'
      ],
      status: 'planned',
      icon: <LocalLaundryService />
    },
    {
      id: 'chemical-system',
      benefits: [
        'integrations_benefit_chemical_tracking',
        'integrations_benefit_cost_optimization',
        'integrations_benefit_safety_compliance', 
        'integrations_benefit_auto_reordering'
      ],
      status: 'planned',
      icon: <Engineering />
    }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h3" component="h1" sx={{ textAlign: 'center', mb: 2, fontWeight: 700 }}>
        {t('integrations_title')}
      </Typography>
      <Typography variant="h6" sx={{ textAlign: 'center', mb: 4, color: 'text.secondary' }}>
        {t('integrations_subtitle')}
      </Typography>

      <Grid container spacing={3}>
        {integrations.map((integration) => (
          <Grid item xs={12} sm={6} lg={4} key={integration.id}>
            <Card 
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
              onClick={() => setSelectedIntegration(integration)}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  {integration.icon}
                </Box>
                
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t(`integrations_${integration.id}_title`)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t(`integrations_${integration.id}_desc`)}
                </Typography>
                
                <Chip 
                  label={t('integrations_status_planned')} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <IntegrationDetails 
        integration={selectedIntegration}
        onClose={() => setSelectedIntegration(null)}
        t={t}
      />
    </Box>
  );
};

const IntegrationDetails = ({ integration, onClose, t }) => {
  if (!integration) return null;

  return (
    <Dialog open={!!integration} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {integration.icon}
          <Typography variant="h5" component="div">
            {t(`integrations_${integration.id}_title`)}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              {t('integrations_dialog_description')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t(`integrations_${integration.id}_desc`)}
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              {t('integrations_dialog_benefits')}
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              {integration.benefits.map((benefit, index) => (
                <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                  {t(benefit)}
                </Typography>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              height: 200, 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                {t('integrations_diagram_placeholder')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{t('integrations_dialog_close')}</Button>
        <Button variant="contained" disabled>
          {t('integrations_dialog_coming_soon')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FutureIntegrations;